import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ThreeDModels, modelsConfig } from '@/lib/threejs/config';
import { useGetLocationsQuery } from '@/services';
import { Pencil, Layers } from 'lucide-react';
import { Switch } from '@/components/ui/switch';

interface VisualizationControlsProps {
  devices: any[];
  selectedDevices: string[];
  setSelectedDevices: (deviceIds: string[]) => void;
  selectedSpace: ThreeDModels;
  setSelectedSpace: (space: ThreeDModels) => void;
  className?: string;
  onEditorModeChange?: (isEditorMode: boolean) => void;
  onSensorTypeFilterChange?: (types: string[]) => void;
  showHeatmap?: boolean;
  onHeatmapChange?: (show: boolean) => void;
}

export function VisualizationControls({
  devices,
  selectedDevices,
  setSelectedDevices,
  selectedSpace,
  setSelectedSpace,
  className,
  onEditorModeChange,
  onSensorTypeFilterChange,
  showHeatmap = false,
  onHeatmapChange,
}: VisualizationControlsProps) {
  const [filterSensorType, setFilterSensorType] = useState<string>('all');
  const [filterLocationId, setFilterLocationId] = useState<string>('all');
  const [isEditorMode, setIsEditorMode] = useState(false);

  useEffect(() => {
    console.log('Controls showHeatmap changed:', showHeatmap);
  }, [showHeatmap]);

  // Get unique sensor types from all devices
  const sensorTypes = new Set<string>();
  const { data: locationsList } = useGetLocationsQuery({});

  devices.forEach((device) => {
    if (device.sensors) {
      device.sensors.forEach((sensor) => {
        if (sensor.type) sensorTypes.add(sensor.type);
      });
    }
  });

  // Filter devices by sensor type
  const filteredDevicesBySensorType =
    filterSensorType === 'all'
      ? devices
      : devices.filter((device) => device.sensors?.some((sensor) => sensor.type === filterSensorType));
  const filteredDevices =
    filterLocationId === 'all'
      ? filteredDevicesBySensorType
      : filteredDevicesBySensorType.filter((device) => device.location_id === filterLocationId);

  // Handle sensor type filter change
  const handleSensorTypeFilterChange = (type: string) => {
    setFilterSensorType(type);
    if (onSensorTypeFilterChange) {
      onSensorTypeFilterChange(type === 'all' ? [] : [type]);
    }
  };

  // Toggle device selection
  const toggleDevice = (deviceId: string) => {
    if (selectedDevices.includes(deviceId)) {
      setSelectedDevices(selectedDevices.filter((id) => id !== deviceId));
    } else {
      setSelectedDevices([...selectedDevices, deviceId]);
    }
  };

  // Select all visible devices
  const selectAllVisible = () => {
    setSelectedDevices(filteredDevices.map((device) => device.id));
  };

  // Deselect all devices
  const deselectAll = () => {
    setSelectedDevices([]);
  };

  // Handle editor mode toggle
  const toggleEditorMode = () => {
    const newMode = !isEditorMode;
    setIsEditorMode(newMode);
    onEditorModeChange?.(newMode);
  };

  return (
    <Card className={className}>
      <CardContent className="p-4 space-y-4">
        <div>
          <Label htmlFor="space-select">3D Environment</Label>
          <Select value={selectedSpace} onValueChange={(value) => setSelectedSpace(value as ThreeDModels)}>
            <SelectTrigger id="space-select" className="mt-1">
              <SelectValue placeholder="Select 3D space" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ThreeDModels.EV_CHARGER}>{modelsConfig.evcharger.name}</SelectItem>
              <SelectItem value={ThreeDModels.GREENHOUSE}>{modelsConfig.greenhouse.name}</SelectItem>
              <SelectItem value={ThreeDModels.RED_HOUSE}>{modelsConfig.redHouse.name}</SelectItem>
              <SelectItem value={ThreeDModels.BEDROOM}>{modelsConfig.bedroom.name}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex justify-between items-center mb-4">
          <Button
            variant={isEditorMode ? 'default' : 'outline'}
            size="sm"
            onClick={toggleEditorMode}
            className="flex items-center gap-2"
          >
            <Pencil className="w-4 h-4" />
            {isEditorMode ? 'Exit Editor' : 'Editor Mode'}
          </Button>
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-muted-foreground" />
            <Label htmlFor="heatmap-toggle" className="text-sm">Heatmap</Label>
            <Switch
              id="heatmap-toggle"
              checked={showHeatmap}
              onCheckedChange={onHeatmapChange}
            />
          </div>
        </div>

        <div>
          <Label>Filter by Location</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            <Badge
              variant={filterLocationId === 'all' ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setFilterLocationId('all')}
            >
              All Locations
            </Badge>
            {locationsList?.data.map((location) => (
              <Badge
                key={location.id}
                variant={filterLocationId === location.id ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setFilterLocationId(location.id)}
              >
                {location.name}
              </Badge>
            ))}
          </div>
        </div>

        <div>
          <Label>Filter by Sensor Type</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            <Badge
              variant={filterSensorType === 'all' ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => handleSensorTypeFilterChange('all')}
            >
              All Types
            </Badge>
            {Array.from(sensorTypes).map((type) => (
              <Badge
                key={type}
                variant={filterSensorType === type ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => handleSensorTypeFilterChange(type)}
              >
                {type}
              </Badge>
            ))}
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <Label>Select Devices</Label>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={selectAllVisible} disabled={filteredDevices.length === 0}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAll} disabled={selectedDevices.length === 0}>
                Clear
              </Button>
            </div>
          </div>

          <div className="max-h-[200px] overflow-y-auto space-y-1 pr-1">
            {filteredDevices.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground text-sm">No devices match the selected filter</div>
            ) : (
              filteredDevices.map((device) => (
                <div
                  key={device.id}
                  className={`
                    p-2 rounded-md flex justify-between items-center cursor-pointer border
                    ${
                      selectedDevices.includes(device.id)
                        ? 'bg-primary/10 border-primary/30'
                        : 'hover:bg-muted border-transparent'
                    }
                  `}
                  onClick={() => toggleDevice(device.id)}
                >
                  <div>
                    <div className="font-medium text-sm">{device.name || `Device ${device.id.substring(0, 8)}`}</div>
                    <div className="text-xs text-muted-foreground">{device.sensors?.length || 0} sensors</div>
                  </div>
                  <Badge variant={selectedDevices.includes(device.id) ? 'default' : 'outline'}>
                    {selectedDevices.includes(device.id) ? 'Selected' : 'Select'}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="text-xs text-muted-foreground">{selectedDevices.length} device(s) selected</div>
      </CardContent>
    </Card>
  );
}
